/*
  <newsletter-popup> — welcome-offer dialog (sections/newsletter-popup.liquid).

  - Opens after `data-delay` seconds, once per visitor; closing it snoozes it
    for `data-days` days (localStorage, so it never nags).
  - If the page reloaded after a sign-up (or with a form error), it opens
    straight away on the "check your inbox" step / the error message.
  - Escape, the backdrop, the × and "No thanks" all close it; focus is kept
    inside while open and returned afterwards.
*/
class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.key = 'melliPopup';
    this.dialog = this.querySelector('.nl-popup-dialog');
    this.onKey = this.onKey.bind(this);

    this.querySelectorAll('[data-nl-close]').forEach((el) => el.addEventListener('click', () => this.close()));

    const succeeded = !!this.querySelector('[data-nl-success]');
    if (succeeded) this.remember('subscribed');

    if (succeeded || this.querySelector('[data-nl-error]')) {
      this.open();
      return;
    }
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.close(true); });
      return;
    }
    if (this.snoozed()) return;
    const delay = Math.max(1, parseInt(this.dataset.delay, 10) || 6) * 1000;
    this.timer = setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKey);
  }

  read() {
    try { return JSON.parse(localStorage.getItem(this.key)) || {}; } catch (e) { return {}; }
  }

  remember(state) {
    try { localStorage.setItem(this.key, JSON.stringify({ state, at: Date.now() })); } catch (e) {}
  }

  snoozed() {
    const saved = this.read();
    if (saved.state === 'subscribed') return true;
    const days = parseInt(this.dataset.days, 10) || 14;
    return saved.state === 'closed' && Date.now() - saved.at < days * 86400000;
  }

  open() {
    if (!this.hidden) return;
    this.lastFocus = document.activeElement;
    this.hidden = false;
    document.documentElement.classList.add('nl-popup-lock');
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.addEventListener('keydown', this.onKey);
    const first = this.querySelector('input[type="email"], .btn');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 50);
  }

  close(silent) {
    if (this.hidden) return;
    this.classList.remove('is-open');
    this.hidden = true;
    document.documentElement.classList.remove('nl-popup-lock');
    document.removeEventListener('keydown', this.onKey);
    if (!silent && this.read().state !== 'subscribed') this.remember('closed');
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  }

  onKey(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab') return;
    const items = [...this.dialog.querySelectorAll('a[href], button, input:not([type="hidden"])')].filter((el) => !el.disabled && el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
